import { ExcelExportParams } from 'ag-grid-community';
import { Component, Input, inject } from '@angular/core';

import { environment } from '../../../environments/environment';
import { GridOptionsService } from '../../columns/column-definition/grid-options.service';
import { SnackbarService } from '../../services/snackbar/snackbar.service';

@Component({
  selector: 'app-export',
  templateUrl: './export.component.html',
  standalone: false,
})
export class ExportComponent {
  private gridOptionsService = inject(GridOptionsService);
  private snackbar = inject(SnackbarService);

  @Input() public rowId: string | undefined;
  @Input() public trademarkName: string | undefined;
  private _disabled = false;
  public buttonDisabled = false;

  @Input()
  public get disabled(): boolean {
    return this._disabled;
  }

  public set disabled(value: boolean) {
    this._disabled = value;
    this.buttonDisabled = value;
  }

  @Input()
  public tooltip = $localize`:@@ExportComponentExportBtnTooltip:Export`;
  @Input() public tooltipPosition: 'above' | 'below' | 'before' | 'after' =
    'below';
  selectedRowCount: number | undefined = 0;

  private imageBase64Map: Map<string, string> = new Map();

  private getBlankImage(): string {
    return 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mP8Xw8AAn0B9Z0qkFoAAAAASUVORK5CYII=';
  }

  async export(): Promise<void> {
    if (!this.disabled) {
      await this.preFetchImages();
      this.gridOptionsService
        .getGridApi()
        ?.exportDataAsExcel(this.getExportParam());
      this.snackbar.openSnackBar(this.getSnackbarMessage());
    }
  }

  private async preFetchImages(): Promise<void> {
    if (this.rowId) {
      const imageUrl = `${environment.backendBaseUrl}/orders/image/${this.rowId}`;
      const base64 = await this.convertImageToBase64(imageUrl);
      if (base64) {
        this.imageBase64Map.set(this.rowId, base64);
      }
    } else {
      const selectedNodes = this.gridOptionsService
        .getGridApi()
        ?.getSelectedNodes();

      if (selectedNodes) {
        const fetchPromises = selectedNodes.map(async (node) => {
          const orderNumber = node.data?.orderNumber;
          if (orderNumber) {
            const imageUrl = `${environment.backendBaseUrl}/orders/image/${orderNumber}`;
            const base64 = await this.convertImageToBase64(imageUrl);
            if (base64) {
              this.imageBase64Map.set(orderNumber, base64);
            }
          }
        });

        await Promise.all(fetchPromises);
      }
    }
  }

  getExportParam(): ExcelExportParams {
    const selectedNodes = this.gridOptionsService
      .getGridApi()
      ?.getSelectedNodes();
    this.selectedRowCount = selectedNodes?.length || 0;
    return {
      fileName: 'export.xlsx',
      sheetName: 'Order Results',
      columnKeys: this.getColumnKeysInDisplayOrder(),
      shouldRowBeSkipped: (param: any) => {
        return this.rowId === undefined
          ? !selectedNodes?.includes(param.node)
          : param.node.id !== this.rowId;
      },
      addImageToCell: (rowIndex, column, value) => {
        if (column.getColId() === 'logo') {
          const base64 = value ? this.imageBase64Map.get(value) : undefined;
          if (base64 && base64.trim().length > 300) {
            return {
              image: {
                id: `image-${rowIndex}-${column.getColId()}`,
                base64,
                imageType: 'png',
              },
              value: undefined,
            };
          }
          return {
            image: {
              id: `image-${rowIndex}-${column.getColId()}-blank`,
              base64: this.getBlankImage(),
              imageType: 'png',
            },
            value: undefined,
          };
        }
        return undefined;
      },
    };
  }

  private getColumnKeysInDisplayOrder(): string[] {
    const gridApi = this.gridOptionsService.getGridApi();
    const columnDefs =
      typeof gridApi?.getColumnDefs === 'function'
        ? gridApi.getColumnDefs()
        : [];
    const columnState = (Array.isArray(columnDefs) ? columnDefs : []).map(
      (col: any) => ({
        colId: (col as any).field,
        pinned: (col as any).pinned,
      }),
    );
    const leftColumnState = columnState
      ?.filter((col: any) => col.pinned === 'left')
      .filter((col: any) => col.colId !== 'checkBox');
    const centerColumnState = columnState?.filter((col: any) => !col.pinned);
    const rightColumnState = columnState?.filter(
      (col: any) => col.pinned === 'right',
    );
    return [
      ...(leftColumnState || []),
      ...(centerColumnState || []),
      ...(rightColumnState || []),
    ].map((m: any) => m.colId);
  }

  private async convertImageToBase64(imageUrl: string): Promise<string | null> {
    try {
      const response = await fetch(imageUrl, {
        credentials: 'include',
      });

      if (!response.ok) {
        return null;
      }
      const blob = await response.blob();
      return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = () => reject('Failed to convert image to base64');
        reader.readAsDataURL(blob);
      });
    } catch {
      return null;
    }
  }

  getSnackbarMessage(): string {
    let msg;
    if (this.rowId === undefined) {
      msg =
        this.selectedRowCount === 1
          ? $localize`:@@ExportComponentBulkSingleExportSnackbarMsg:Exported file for ${this.selectedRowCount} selected order.`
          : $localize`:@@ExportComponentBulkMultiExportSnackbarMsg:Exported file for ${this.selectedRowCount} selected orders.`;
    } else {
      msg = $localize`:@@ExportComponentSelectedExportSnackbarMsg:Exported file for the order "${this.trademarkName}".`;
    }
    return msg;
  }
}
